<template>
  <div class="tender-query-accordion">
    <ul
      id="tender-query-accordion"
      class="accordion"
      data-accordion
      data-deep-link="true"
      data-allow-all-closed="true"
      data-multi-expand="true"
      data-update-history="true"
      data-deep-link-smudge="500"
    >
      <template v-for="(query, index) in queries">
        <li
          class="accordion-item"
          :id="`tender-query-${index}`"
          :class="{'is-active': index === 0 }"
          :key="index"
          data-accordion-item
        >

          <a
            :key="index"
            class="accordion-title"
            href="javascript:;"
          >
            {{ $t('summary.query.name') }} {{ query.order }}
            <div
              class="options"
              v-if="showOptions(query)"
            >
              <dotted-dropdown :id="`tender-query-dotted-dropdown-${index}`">
                <li v-if="showOptions(query)">
                  <DialogSlot
                    :id="modalId"
                    ref="cancelQueryRef"
                    :beforeOpenModal="() => checkQueryEditable(query.id, index)"
                    :modalBtnClass="'btn--transparent'"
                    :modalBtn="`${$t('action.cancel')} ${$t('summary.query.name')}`"
                    :showCloseBtn="false"
                  >
                    <div
                      slot="content"
                      class="slot-content"
                    >
                      <span class="font-bold">{{ $t('hint.cancelQuery') }}</span>
                    </div>
                    <div slot="buttons">
                      <button
                        type="button"
                        class="btn btn--darkblue close-reveal-modal"
                        @click="closeModal()"
                      >{{ $t('action.goback') }}</button>
                      <button
                        type="button"
                        class="btn btn--orange close-reveal-modal"
                        @click="deleteQuery(query.id)"
                      >{{ $t('action.yesContinue') }}</button>
                    </div>
                  </DialogSlot>
                </li>
              </dotted-dropdown>
            </div>
            <div
              class="uppercase-bold query-status"
              v-show="showStatus(query.status)"
              :class="statusClass(query.status)"
            >
              <div>
                {{ getStatusText(query.status) }}
              </div>
            </div>
          </a>
          <div
            class="accordion-content"
            data-tab-content
            :id="`tender-query-accordion-${index}`"
          >
            <TenderQuery
              @queryCancelEvent="cancelQueryEdit"
              :withCancelBtn="withEditButton(query.status)"
              v-if="editMode(query.status)"
              :query="query"
              :index="index"
              :requestIssue="requestIssue"
            />
            <TenderQueryView
              :ref="`tenderQueryView-${index}`"
              @queryEditEvent="onQueryEditEvent"
              :withEditBtn="withEditButton(query.status)"
              v-if="!editMode(query.status)"
              :query="query"
            />
          </div>
        </li>
      </template>
    </ul>
  </div>
</template>

<script>
import TenderQuery from '@/modules/tenders/queries/TenderQuery'
import TenderQueryView from '@/modules/tenders/queries/TenderQueryView'
import DottedDropdown from '@/controls/DottedDropdown'
import DialogSlot from '@/controls/DialogSlot'

import { queryStatus, queryStatusMap } from '@/utils/locale'
import { mapGetters, mapActions } from 'vuex'
import EventBus from '@/utils/eventBus'
import { FORM_SRC_TENDER_QUERY } from '@/utils/consts'
import { utils as formUtils } from '@/plugin/InputChange'
import { getRandomIdSuffix } from '@/utils/vueCompHelper'

export default {
  name: 'tenderQueryAccordion',
  components: {
    TenderQuery,
    TenderQueryView,
    DottedDropdown,
    DialogSlot,
  },

  data () {
    return {
      modalId: `tq-cxl-draft-mdl-${getRandomIdSuffix()}`,
      isEditMode: false,
      requestIssue: false,
    }
  },

  methods: {
    ...mapActions('tenders', {
      cancelQuery: 'deleteQuery',
      refreshCurrentBid: 'refreshCurrentBid',
    }),

    statusClass (status) {
      return status ? status.toLowerCase() : 'status'
    },
    showStatus (status) {
      return status && status !== queryStatus.DRAFT
    },
    getStatusText (status) {
      let statusText
      if (this.isContractor && status === queryStatus.ISSUED) {
        statusText = queryStatusMap[status]
      } else {
        statusText = status === queryStatus.NO_RESPONSE ? queryStatusMap.NO_RESPONSE : status
      }
      return statusText
    },

    editMode (status) {
      return ((this.isContractor && !this.isRepresentative) && status === queryStatus.ISSUED) ||
        ((this.isPIC || this.isRepresentative) && status === queryStatus.DRAFT && this.isEditMode)
    },
    onQueryEditEvent (param) {
      this.isEditMode = true
      this.requestIssue = !!param.requestIssue
    },
    withEditButton (status) {
      return (this.isPIC || this.isRepresentative) && status === queryStatus.DRAFT
    },

    initAccordion () {
      if (this.accordion) {
        if ($('#tender-query-accordion').childElementCount > 0) {
          this.accordion.destroy && this.accordion.destroy()
        }
        delete this.accordion
      }
      if (!this.accordion) {
        this.accordion = new Foundation.Accordion($('#tender-query-accordion'))
      }
    },

    refreshTab () {
      setTimeout(this.initAccordion, 0)
    },

    closeModal () {
      this.$refs.cancelQueryRef[0].closeModal()
    },

    showOptions (query) {
      return this.isPIC && query.status === queryStatus.DRAFT
    },
    async checkQueryEditable (id, index) {
      if (this.isEditMode) {
        // Should skip if the current user is editing
        return true
      }
      const tenderQueryView = this.$refs[`tenderQueryView-${index}`][0]
      await tenderQueryView.checkEditable(id)
      if (!tenderQueryView.editable) {
        $('.js-dropdown-active').removeClass('js-dropdown-active') // To close the opened dot options menu
        return false
      }
      return true
    },
    async deleteQuery (id) {
      await this.cancelQuery(id)
      this.closeModal()
      await this.refreshCurrentBid()
      formUtils.resetDirty(FORM_SRC_TENDER_QUERY)
      if (this.queries.length > 0) {
        this.refreshTab()
      } else {
        EventBus.$emit('refresh:bid-detail')
      }
    },
    async cancelQueryEdit () {
      await this.refreshCurrentBid()
      this.isEditMode = false
    },
  },

  destroyed () {
    EventBus.$off('refresh:tender-query')
    if (this.accordion) {
      this.accordion.destroy()
    }
  },

  mounted () {
    EventBus.$on('refresh:tender-query', async () => {
      this.refreshTab()
      if (this.queries.length) {
        $('#tender-query-0').addClass('is-active')
      }
    })
    this.initAccordion()
  },

  computed: {
    ...mapGetters('user', ['currentUser', 'isContractor']),
    ...mapGetters('tenders', ['currentTender', 'currentBid', 'isRepresentative']),

    isPIC () {
      return deep(this.currentTender, 'general.managedBy') === this.currentUser.id
    },

    queries () {
      return this.currentBid.queries || []
    },
  },

  watch: {
    queries () {
      setTimeout(() => this.initAccordion(), 0)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/common';
@import "~styles/accordion";
.query-order {
  display: inline-block;
  margin: 0;
}

.options {
  display: inline-block;
  vertical-align: text-top;
  z-index: 100;
  width: 20px;
  margin: 0;
  height: 20px;
  background-color: #F8F8F8;
}

.query-status {
  margin-right: 60px;
  line-height: 30px;
  display: inline-block;
}
</style>

<style lang="scss">
.options {
  .tender-ellipsis-size {
    margin: 0!important;
    padding: 0 10px;
  }
}
</style>
